import * as React from "react"

import { useToast } from "@chakra-ui/react"

import { AdminPermission } from "@prisma/client"

import { intersects } from "@supliful/domain"

import { useMe } from "lib/hooks/useMe"

/**
 * Authorization gate for a page. Authentication, loading, and the session
 * itself are handled by AuthProvider at the app root, so this only checks
 * that the (already resolved, non-null) admin holds one of `permissions`.
 * A page with no required permissions renders for any authenticated admin.
 */
export const withAuth = <P extends object>(
  Page: React.ComponentType<P>,
  permissions: AdminPermission[] = [],
) => {
  return function AuthorizedPage(props: P) {
    const { me } = useMe()
    const toast = useToast()

    const isAuthorized =
      permissions.length === 0 ||
      intersects([AdminPermission.EVERYTHING, ...permissions], me.permissions)

    React.useEffect(() => {
      if (!isAuthorized) {
        toast({ status: "error", description: "Not Authorized" })
      }
    }, [isAuthorized, toast])

    if (!isAuthorized) return <></>
    return <Page {...props} />
  }
}
